/**
 * Resultados de busqueda (BUS-01, BUS-06).
 *
 * Lo importante de esta pantalla no es cuando encuentra: es cuando no
 * encuentra. Una busqueda sin resultado no puede terminar en "0 productos" —
 * ahi se pierde la venta. Termina en WhatsApp, con el termino ya escrito, que
 * es donde hoy se resuelve.
 *
 * No se indexa: son URL infinitas generadas por el visitante y no aportan nada
 * al catalogo en Google.
 */
import { useSearchParams } from 'react-router-dom';

import { GrillaProductos } from '../componentes/catalogo/GrillaProductos';
import { AvisoError, Cargando } from '../componentes/comunes/EstadoPeticion';
import { Icono } from '../componentes/comunes/Icono';
import { Buscador } from '../componentes/diseno/Buscador';
import { BUSQUEDA_FOTO_HABILITADA } from '../configuracion/busqueda-foto';
import { usePeticion } from '../hooks/use-peticion';
import { Seo } from '../seo/Seo';
import { buscar } from '../servicios/busqueda.servicio';
import { registrarClicWhatsapp } from '../servicios/metricas.servicio';
import { numero } from '../utilidades/formato';
import { pedirBusquedaPorFoto } from '../utilidades/busqueda-foto-atajo';
import { rutas } from '../utilidades/rutas';
import { whatsappBusqueda } from '../utilidades/whatsapp';

export function Busqueda() {
  const [parametros] = useSearchParams();
  const termino = parametros.get('termino') ?? '';
  const pagina = Number(parametros.get('pagina') ?? '1');

  const { estado, datos, error } = usePeticion(
    (senal) => buscar(termino, { pagina, senal }),
    [termino, pagina],
  );

  return (
    <>
      <Seo
        titulo={termino.length > 0 ? `Resultados para "${termino}"` : 'Buscar en el catalogo'}
        descripcion={`Resultados de busqueda en el catalogo de Bandu para "${termino}".`}
        ruta={rutas.busqueda(termino)}
        sinIndexar
      />

      <div className="contenedor pagina">
        <header className="encabezado-pag">
          <div>
            <h1>
              {termino.length > 0 ? <>Resultados para «{termino}»</> : 'Buscar en el catalogo'}
            </h1>
            {datos !== null && !datos.sinResultados ? (
              <p className="sub">
                {numero(datos.total)} {datos.total === 1 ? 'producto' : 'productos'}
              </p>
            ) : null}
          </div>
        </header>

        {/*
          Cuando lo escrito no encontraba nada y el buscador corrigio un error de
          tipeo, la pantalla lo DICE. Sin este aviso, quien escribio "bisgara" y
          ve bisagras no sabe si el sitio le entendio o le esta mostrando
          cualquier cosa — y no hay forma de que lo averigue.

          El texto dice "buscaste X" y no "no encontramos X" porque lo escrito
          puede haber encontrado algo: se corrige tambien cuando encontro muy
          poco, y ahi "no encontramos" seria falso.

          No se ofrece "buscar «bisgara» de todas formas", que es lo que hace
          Google: ahi ese enlace lleva a resultados peores, pero acá llevaria
          exactamente a cero, que fue lo que paso antes de corregir.
        */}
        {datos !== null && datos.terminoCorregido !== null ? (
          <p className="aviso-correccion">
            <Icono nombre="info" />
            <span>
              Buscaste «{datos.termino}». Te mostramos resultados para{' '}
              <strong>«{datos.terminoCorregido}»</strong>.
            </span>
          </p>
        ) : null}

        <div className="buscador-pagina">
          <Buscador
            id="buscador-resultados"
            marcador="Busca por nombre, codigo o para que lo necesitas…"
            inicial={termino}
          />
        </div>

        {estado === 'cargando' ? <Cargando que="los resultados" /> : null}
        {estado === 'error' ? <AvisoError error={error} /> : null}

        {datos !== null && datos.sinResultados ? (
          <section className="rescate" aria-labelledby="sin-resultados">
            <h2 id="sin-resultados">
              <Icono nombre="lupaVacia" />
              No encontramos «{termino}» en el catalogo en linea
            </h2>
            <p>
              Puede que lo tengamos en tienda sin publicar todavia, o que se llame distinto en el
              rubro.{' '}
              {BUSQUEDA_FOTO_HABILITADA
                ? 'Si tienes la pieza a mano, buscala por foto; si no, escribenos'
                : 'Escribenos'}{' '}
              y te confirmamos al tiro: el mensaje ya va con lo que buscaste.
            </p>
            <p className="acc">
              <a
                className="btn btn-wa"
                href={whatsappBusqueda(termino)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => registrarClicWhatsapp({ origen: 'busqueda', termino })}
              >
                <Icono nombre="wa" />
                Preguntar por WhatsApp
              </a>
              {/*
                Que se llame distinto en el rubro es exactamente el caso que la
                busqueda por foto resuelve, asi que el camino corto va aca.
              */}
              {BUSQUEDA_FOTO_HABILITADA ? (
                <button type="button" className="btn" onClick={pedirBusquedaPorFoto}>
                  <Icono nombre="camara" />
                  Buscar por foto
                </button>
              ) : null}
            </p>
          </section>
        ) : null}

        {datos !== null && !datos.sinResultados ? (
          <GrillaProductos productos={datos.productos} titulo={`Resultados para ${termino}`} titula />
        ) : null}
      </div>
    </>
  );
}
